/* Overlay system (docs/15 §9). Native <dialog class="overlay"> opened by
   <button data-overlay-open="{id}">. The layout (popup / drawer / sheet) is pure CSS in
   overlay.css; this module adds behaviour on top of showModal():
   - one overlay at a time (an overlay inside another, or one marked data-overlay-nested, stacks)
   - backdrop click, [data-overlay-close], focus return to the trigger, aria-expanded
   - mw:overlay:open / mw:overlay:close events (bubble to document, detail { id })
   - swipe down to close bottom sheets
   - theme editor: selecting a block or section inside an overlay opens it
   API: window.Munkwell.overlay.open(id, trigger) / .close(id) — close() without id closes the top one. */

const OVERLAY = 'dialog.overlay';
const mobile = window.matchMedia('(max-width: 767px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const stack = []; // open overlays, bottom → top
const returnTo = new WeakMap(); // overlay → element that gets focus back
const handedOver = new WeakSet(); // closed to make room for another overlay: no focus return

const resolve = (ref) => {
  const el = typeof ref === 'string' ? document.getElementById(ref) : ref;
  return el instanceof HTMLDialogElement ? el : null;
};

// Drops overlays that closed or left the DOM (a theme-editor re-render removes them silently).
const prune = () => {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    if (!stack[i].isConnected || !stack[i].open) stack.splice(i, 1);
  }
};

/* Triggers that opened an overlay through the API rather than [data-overlay-open] — the collection
   page's filter button is one. Without this they kept aria-expanded="false" while their sheet was
   open (measured 20.09 on /collections/all). */
const apiTriggers = new WeakMap(); // overlay → trigger element carrying aria-expanded

const setExpanded = (dialog, expanded) => {
  const trigger = apiTriggers.get(dialog);
  if (trigger?.isConnected) trigger.setAttribute('aria-expanded', String(expanded));
  if (!expanded) apiTriggers.delete(dialog);
  if (!dialog.id) return;
  document.querySelectorAll(`[data-overlay-open="${CSS.escape(dialog.id)}"]`).forEach((el) => {
    el.setAttribute('aria-expanded', String(expanded));
  });
};

const emit = (dialog, type) => {
  dialog.dispatchEvent(new CustomEvent(`mw:overlay:${type}`, { bubbles: true, detail: { id: dialog.id } }));
};

// Closes an overlay and everything stacked above it.
function dismiss(dialog, handover = false) {
  if (!dialog?.open || !dialog.isConnected) return;
  const index = stack.indexOf(dialog);
  if (index > -1) stack.slice(index + 1).reverse().forEach((above) => dismiss(above, true));
  if (handover) handedOver.add(dialog);
  dialog.close();
}

function open(ref, trigger = null, { instant = false } = {}) {
  const dialog = resolve(ref);
  if (!dialog?.isConnected) return false;
  if (dialog.open) return true;
  prune();

  // A dialog inside a closed overlay can't render: open its host first.
  const host = dialog.parentElement?.closest(OVERLAY);
  if (host && !host.open && !open(host)) return false;

  const nested = dialog.hasAttribute('data-overlay-nested') || Boolean(trigger?.hasAttribute?.('data-overlay-nested'));
  if (!nested) {
    // One at a time — but an overlay can't close one that contains it.
    [...stack].reverse().forEach((other) => {
      if (!other.contains(dialog)) dismiss(other, true);
    });
  }

  // If the trigger sat in an overlay that just closed, focus goes back to where that one came from.
  let back = trigger instanceof HTMLElement ? trigger : document.activeElement;
  const from = back?.closest?.('dialog');
  if (from && !from.open) back = returnTo.get(from) ?? null;
  returnTo.set(dialog, back instanceof HTMLElement ? back : null);

  dialog.classList.toggle('is-instant', instant);
  try {
    dialog.showModal();
  } catch {
    dialog.classList.remove('is-instant');
    return false;
  }
  stack.push(dialog);
  if (trigger instanceof HTMLElement && trigger.hasAttribute('aria-expanded') && !trigger.hasAttribute('data-overlay-open')) {
    apiTriggers.set(dialog, trigger);
  }
  setExpanded(dialog, true);
  emit(dialog, 'open');
  if (instant) requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.remove('is-instant')));
  return true;
}

function close(ref) {
  prune();
  const dialog = ref == null ? stack.at(-1) : resolve(ref);
  if (!dialog?.open) return false;
  dismiss(dialog);
  return true;
}

/* ---------- Close bookkeeping (Escape, buttons, forms, API all end here) ---------- */
// `close` doesn't bubble; a capturing listener on document still sees every dialog.
document.addEventListener(
  'close',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    if (!dialog.matches(OVERLAY) && !stack.includes(dialog)) return;

    const index = stack.indexOf(dialog);
    if (index > -1) stack.splice(index, 1);
    prune();
    endDrag(dialog);
    setExpanded(dialog, false);

    const back = returnTo.get(dialog);
    const handover = handedOver.has(dialog);
    returnTo.delete(dialog);
    handedOver.delete(dialog);
    const top = stack.at(-1);
    if (!handover && back?.isConnected && (!top || top.contains(back))) back.focus({ preventScroll: true });

    emit(dialog, 'close');
  },
  true
);

/* ---------- Escape ----------
   A modal <dialog> closes itself on Escape — except when Chrome's close-watcher rules decide the
   key press was not a fresh user action (measured 21.09: both the cart drawer and the lightbox
   stayed open). So the top overlay is closed here as well; when the browser already did it,
   this finds the dialog closed and does nothing. */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || event.defaultPrevented) return;
  prune();
  const top = stack.at(-1);
  if (!top?.open) return;
  event.preventDefault();
  dismiss(top);
});

/* ---------- Triggers, close buttons, backdrop ---------- */
let pressed = null; // where the pointer went down — a text selection dragged out of the panel isn't a backdrop click

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const opener = target.closest('[data-overlay-open]');
  if (opener) {
    // Links keep working as links for new-tab clicks and when the overlay isn't on the page.
    if (opener.matches('a[href]') && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    // preventDefault also cancels a native commandfor/command="show-modal" on the same button.
    if (open(opener.getAttribute('data-overlay-open'), opener)) event.preventDefault();
    return;
  }

  const closer = target.closest('[data-overlay-close]');
  if (closer) {
    const ref = closer.getAttribute('data-overlay-close');
    const dialog = (ref && resolve(ref)) || closer.closest('dialog');
    if (dialog?.open) {
      event.preventDefault();
      dismiss(dialog);
    }
    return;
  }

  if (target === pressed && target.matches(`${OVERLAY}[open]`)) dismiss(target);
});

/* ---------- Swipe down to close sheets ---------- */
let drag = null;

function startDrag(event) {
  if (!event.isPrimary || event.button !== 0 || !mobile.matches) return;
  const head = event.target.closest?.('.overlay__head');
  if (!head || event.target.closest('a, button, input, select, textarea, label')) return;
  const dialog = head.closest(OVERLAY);
  const panel = head.closest('.overlay__panel');
  if (!dialog?.open || !panel || dialog.dataset.mobile !== 'sheet') return;

  drag = { dialog, panel, pointer: event.pointerId, startY: event.clientY, y: event.clientY, t: event.timeStamp, dy: 0, v: 0, height: panel.offsetHeight || 1 };
  try {
    head.setPointerCapture(event.pointerId);
  } catch {
    /* pointer already released */
  }
  // Reduced motion: the gesture still closes the sheet, the panel just doesn't follow the finger.
  if (!reducedMotion.matches) dialog.classList.add('is-dragging');
}

function moveDrag(event) {
  if (!drag || event.pointerId !== drag.pointer) return;
  const elapsed = event.timeStamp - drag.t;
  if (elapsed > 0) drag.v = (event.clientY - drag.y) / elapsed;
  drag.y = event.clientY;
  drag.t = event.timeStamp;
  drag.dy = Math.max(0, event.clientY - drag.startY);
  if (reducedMotion.matches) return;
  drag.panel.style.transform = `translateY(${drag.dy}px)`;
  drag.dialog.style.setProperty('--overlay-drag', Math.min(drag.dy / drag.height, 1).toFixed(3));
}

// Clears the drag styles; the panel's own transition then carries it home or out.
function endDrag(dialog) {
  if (drag?.dialog === dialog) drag = null;
  dialog.classList.remove('is-dragging');
  dialog.style.removeProperty('--overlay-drag');
  dialog.querySelector('.overlay__panel')?.style.removeProperty('transform');
}

function releaseDrag(event) {
  if (!drag || event.pointerId !== drag.pointer) return;
  const { dialog, dy, v, height } = drag;
  const shouldClose = event.type === 'pointerup' && (dy > Math.min(height * 0.3, 160) || (v > 0.5 && dy > 24));
  endDrag(dialog);
  if (shouldClose) dismiss(dialog);
}

document.addEventListener(
  'pointerdown',
  (event) => {
    pressed = event.target;
    startDrag(event);
  },
  true
);
document.addEventListener('pointermove', moveDrag, { passive: true });
document.addEventListener('pointerup', releaseDrag);
document.addEventListener('pointercancel', releaseDrag);

/* ---------- Theme editor ---------- */
// An overlay opens when the selected block/section sits inside it, when the selected block
// declares it (data-overlay-select="{id}", e.g. the Popup block's trigger wrapper), or when a
// selected section's top-level element is or declares one (overlay-group sections).
const editorOpened = new Set();
const pendingClose = new Set();
const reopenIds = new Set();
let editorTimer = 0;

const chainOf = (dialog) => {
  const chain = [];
  for (let node = dialog; node; node = node.parentElement?.closest(OVERLAY) ?? null) chain.unshift(node);
  return chain;
};

function editorOverlay(node, isSection) {
  if (!(node instanceof Element)) return null;
  const inside = node.closest(OVERLAY);
  if (inside) return inside;
  const owner = node.matches('[data-overlay-select]')
    ? node
    : isSection && node.querySelector(`:scope > [data-overlay-select], :scope > ${OVERLAY}`);
  if (!owner) return null;
  return owner.matches(OVERLAY) ? owner : resolve(owner.getAttribute('data-overlay-select'));
}

function editorSelect(event, isSection) {
  const dialog = editorOverlay(event.target, isSection);
  const keep = dialog ? chainOf(dialog) : [];
  keep.forEach((overlay) => {
    pendingClose.delete(overlay);
    if (overlay.open || open(overlay)) editorOpened.add(overlay);
  });
  // Selecting a block elsewhere closes editor-opened overlays at once. A section select without
  // an overlay of its own leaves them to the pending deselect, since the editor also selects the
  // section around a selected block.
  if (isSection && !dialog) return;
  editorOpened.forEach((opened) => {
    if (keep.includes(opened)) return;
    dismiss(opened, true);
    editorOpened.delete(opened);
  });
}

// The next select follows a deselect almost at once; wait, so moving between blocks inside the
// same overlay doesn't close and reopen it.
function editorDeselect() {
  editorOpened.forEach((opened) => pendingClose.add(opened));
  clearTimeout(editorTimer);
  editorTimer = setTimeout(() => {
    pendingClose.forEach((opened) => {
      dismiss(opened, true);
      editorOpened.delete(opened);
    });
    pendingClose.clear();
  }, 120);
}

document.addEventListener('shopify:block:select', (event) => editorSelect(event, false));
document.addEventListener('shopify:section:select', (event) => editorSelect(event, true));
document.addEventListener('shopify:block:deselect', editorDeselect);
document.addEventListener('shopify:section:deselect', editorDeselect);

// A setting change re-renders the section: keep an open overlay open, without replaying its entrance.
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.(`${OVERLAY}[open]`).forEach((dialog) => {
    if (dialog.id) reopenIds.add(dialog.id);
    editorOpened.delete(dialog);
    pendingClose.delete(dialog);
  });
});

document.addEventListener('shopify:section:load', (event) => {
  prune();
  reopenIds.forEach((id) => {
    const dialog = document.getElementById(id);
    if (!(dialog instanceof HTMLDialogElement) || !event.target.contains(dialog)) return;
    reopenIds.delete(id);
    chainOf(dialog).forEach((overlay) => {
      if (overlay.open || open(overlay, null, { instant: true })) editorOpened.add(overlay);
    });
  });
});

/* ---------- Public API ---------- */
window.Munkwell = window.Munkwell || {};
window.Munkwell.overlay = { open, close };
